import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, CalendarDays, ChevronLeft, ChevronRight, Clock, Coffee, Pause, Sparkles, UserX } from 'lucide-react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatMinutes, formatMonth } from '../lib/format';
import { ShiftsTable, type ShiftHistory } from './Attendance';
import { Button, Card, CardHeader, EmptyState, ErrorState, LoadingBlock, Metric, PageHeader } from '../components/ui';

/** "2026-10": a month as the page's address keeps it. */
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

const monthOf = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

function addMonths(month: string, delta: number): string {
  const [year, number] = month.split('-').map(Number);
  return monthOf(new Date(year, number - 1 + delta, 1));
}

/**
 * Route: `/attendance/employees/:id` — an employee's shifts in one month, this
 * month unless `?month=2026-09` says otherwise, opened from their name on the
 * team board (operator, 2026-10-27). The table is My shift's Recent shifts, and
 * each day opens the shift's page the same way. The month's totals sit above it.
 * A manager of theirs reads it — or the person themselves (`GET /attendance/history`).
 */
export function AttendanceEmployeePage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const location = useLocation();
  const [params, setParams] = useSearchParams();

  const current = monthOf(new Date());
  const asked = params.get('month') ?? '';
  const month = MONTH.test(asked) ? asked : current;
  const [year, number] = month.split('-').map(Number);
  const lastDay = new Date(Date.UTC(year, number, 0)).getUTCDate();
  // The board as it was left — its day and department — or the board itself.
  const back = (location.state as { back?: string } | null)?.back ?? '/attendance?tab=team';

  const shifts = useQuery({
    queryKey: ['attendance', 'history', id, month],
    queryFn: () => api.get<ShiftHistory>('/attendance/history', { userId: id, from: `${month}-01`, to: `${month}-${lastDay}` }),
    // The month before stays on screen while the next one loads — never someone else's.
    placeholderData: (previous) => (previous?.user.id === id ? previous : undefined),
  });

  // The month is in the address, so a shift's page comes back to it; the way back to the board goes along.
  const go = (delta: number) => {
    const next = addMonths(month, delta);
    setParams(next === current ? {} : { month: next }, { replace: true, state: location.state });
  };

  const data = shifts.data;
  if (shifts.isLoading) return <LoadingBlock />;
  if (shifts.error || !data) return <ErrorState error={shifts.error} onRetry={() => void shifts.refetch()} />;

  const items = data.items;
  const sum = (pick: (s: (typeof items)[number]) => number | undefined) => items.reduce((total, s) => total + (pick(s) ?? 0), 0);
  const worked = data.totals?.regularMinutes ?? sum((s) => (s.kind === 'FREE' ? 0 : s.summary.workedMinutes));
  const breaks = sum((s) => s.summary.breakMinutes);
  const workBreaks = sum((s) => s.summary.workBreakMinutes);
  const paused = sum((s) => s.summary.pausedMinutes);
  const notWorking = sum((s) => s.summary.notWorkingMinutes);
  const overrun = sum((s) => s.summary.overrunMinutes);
  // The Regular Shifts' days, and the Free Shifts apart (CR-006): worked above is the Regular figure, as payroll reads it.
  const days = new Set(items.filter((s) => s.kind !== 'FREE').map((s) => s.workDate)).size;
  const monthName = formatMonth(year, number, locale);

  return (
    <>
      <div className="mb-4">
        <Link to={back} className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('attendance.title')}
        </Link>
      </div>
      <PageHeader
        title={localized(data.user)}
        subtitle={data.user.department ? localized(data.user.department) : undefined}
        action={
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              aria-label={t('attendance.previousMonth')}
              title={t('attendance.previousMonth')}
              icon={<ChevronLeft className="h-4 w-4 flip-rtl" />}
              onClick={() => go(-1)}
            />
            <span className="min-w-[160px] text-center text-title text-[var(--text-primary)]" aria-live="polite">
              {monthName}
            </span>
            {/* Nothing is worked ahead of time: no month past this one. */}
            <Button
              size="sm"
              aria-label={t('attendance.nextMonth')}
              title={t('attendance.nextMonth')}
              icon={<ChevronRight className="h-4 w-4 flip-rtl" />}
              disabled={month >= current}
              onClick={() => go(1)}
            />
          </div>
        }
      />

      <div className="space-y-5">
        <div className={paused > 0 ? 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6' : 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5'}>
          <Metric label={t('attendance.daysWorked')} value={days} icon={<CalendarDays className="h-4 w-4" />} />
          <Metric label={t('attendance.worked')} value={formatMinutes(worked, locale)} icon={<Clock className="h-4 w-4" />} />
          {/* Paused time is not worked time — and no break (2026-10-24). */}
          {paused > 0 && <Metric label={t('attendance.paused')} value={formatMinutes(paused, locale)} icon={<Pause className="h-4 w-4" />} />}
          <Metric
            label={t('attendance.breaks')}
            value={formatMinutes(breaks, locale)}
            hint={workBreaks ? t('attendance.workBreaks', { minutes: formatMinutes(workBreaks, locale) }) : undefined}
            icon={<Coffee className="h-4 w-4" />}
          />
          <Metric
            label={t('attendance.notWorking')}
            value={formatMinutes(notWorking, locale)}
            icon={<UserX className="h-4 w-4" />}
            tone={notWorking > 0 ? 'error' : 'neutral'}
          />
          <Metric label={t('attendance.overrun')} value={formatMinutes(overrun, locale)} tone={overrun > 0 ? 'warning' : 'neutral'} />
        </div>
        {/* Free Shifts and Total tracked, beside the month's hours and never in them (CR-006). */}
        {data.totals && data.totals.free.count > 0 && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Metric
              label={t('attendance.freeShifts')}
              value={formatMinutes(data.totals.freeMinutes, locale)}
              hint={data.totals.free.count === 1 ? t('attendance.freeOne') : t('attendance.freeTotal', { n: data.totals.free.count })}
              icon={<Sparkles className="h-4 w-4" />}
            />
            <Metric
              label={t('attendance.trackedTotal')}
              value={formatMinutes(data.totals.totalMinutes, locale)}
              hint={t('attendance.trackedTotalHint')}
            />
          </div>
        )}

        <Card padded={false}>
          <CardHeader title={t('attendance.monthShifts')} subtitle={monthName} className="p-5 pb-0" />
          {items.length === 0 ? (
            <EmptyState title={t('attendance.noShiftsInMonth', { month: monthName })} hint="" />
          ) : (
            <ShiftsTable items={items} back={`${location.pathname}${location.search}`} />
          )}
        </Card>
      </div>
    </>
  );
}
